// types
import { i18nEaiDevices } from 'models/i18n';

// constants
import {
  CharacteristicsRk,
  DeviceInstallationStatus
} from 'utils/characteristic';
import { DeviceReservedCapacityGuardingCharacteristicsValidationArguments } from 'views/modules/Eai/Devices/DeviceDetail/Characteristics/Common/DeviceReservedCapacityGuardingUtils';

// ==============================|| I18N SK DEVICES ||============================== //

const skEaiDevices: i18nEaiDevices = {
  createDeviceWizard: {
    title: 'Vytvorenie zariadenia',
    step01: 'Základné informácie',
    step02: 'Výber OOM',
    save: 'Vytvoriť zariadenie'
  },
  input: {
    DeviceId: 'ID zariadenia',
    DeviceName: 'Názov zariadenia',
    DeviceRole: 'Rola zariadenia',
    ProductionDeviceType: 'Typ zariadenia',
    DeviceDeliveryPoint: 'Odberné miesto',
    DeviceDescription: 'Popis zariadenia',
    DeviceCharacteristicGroupName: 'Názov skupiny zariadenia',
    DeviceCharacteristicGroupDescription: 'Popis skupiny zariadenia',
    DeliveryPointName: 'Názov odberného miesta',
    GatewayOfDispatcherSystem: 'Komunikačná brána',
    ServiceCode: 'Účel skupiny'
  },
  deviceList: {
    searchInput: 'Zadajte názov zariadenia',
    createEntityButton: 'Pridať zariadenie',
    columns: {
      deviceId: 'ID zariadenia',
      name: 'Názov zariadenia',
      deliveryPointName: 'Názov OOM',
      installationStatus: 'Stav inštalácie'
    },
    filter: {
      status: 'Status',
      deliveryPointName: 'Názov OOM'
    }
  },
  validation: {
    'DeviceName-required': 'Meno/názov zariadenia je povinný',
    'DeviceRole-required': 'Rola zariadenia je povinná',
    'ProductionDeviceType-required': 'Typ zariadenia je povinný',
    'DeviceDeliveryPoint-required': 'Odberné miesto je povinné',
    'DeviceName-max': 'Prekročená maximálna dĺžka',
    'DeviceCharacteristicGroupName-required':
      'Názov skupiny zariadenia je povinný',
    'DeviceCharacteristicGroupName-length':
      'Maximálna dĺžka názvu skupiny zariadenia je 256 znakov'
  },
  deviceDetail: {
    deviceData: {
      title: 'Informácie o zariadení',
      basicInfo: 'Základné informácie',
      installationStatusSwitch: 'Nainštalované',
      installationStatus: {
        [DeviceInstallationStatus.INSTALLED]: 'Inštalovať',
        [DeviceInstallationStatus.NOT_INSTALLED]: 'Odinštalovať'
      },
      cancelGDSAssignmentValue: 'Odobrať GDS'
    },
    deviceCharacteristics: {
      title: 'Vlastnosti',
      criticalCharacteristicsTitle:
        '* Kritická vlastnosť potrebná pre fungovanie optimalizačného modelu',
      nonCriticalCharacteristicsTitle:
        '** Nekritická vlastnosť. V prípade, že vlastnosť nie je vyplnená, zariadenie bude ignorované optimalizačným modelom',
      deviceTechnicalCharacteristics: {
        title: 'Technické parametre',
        input: {
          panel_type: 'Typ panelu',
          panel_count: 'Počet panelov',
          panel_area: 'Plocha panelu',
          panel_efficiency: 'Účinnosť nového panelu',
          panel_degradation: 'Degradácia panelu za rok',
          panel_degradation_y1: 'Degradácia panelu po prvom roku',
          empirical_coefficient_u0:
            'U0 - Empirický koeficient podľa typu panelu',
          empirical_coefficient_u1:
            'U1 - Empirický koeficient podľa typu panelu',
          max_power_output: 'Maximálny výkon',
          tkmo: 'Teplotný koeficient max. výkonu',
          converter_efficiency: 'Účinnosť meniča',
          cssc: 'Prierez prívodných káblov',
          feeder_losses: 'Straty na prívodoch',
          stc_irradiation: 'STC - ožiarenie',
          stc_temperature: 'STC - teplota',
          notc_irradiation: 'NOCT - ožiarenie',
          notc_temperature: 'NOCT - teplota',
          latitude: 'Zemepisná šírka',
          longitude: 'Zemepisná dĺžka',
          elevation: 'Nadmorská výška',
          panel_azimuth: 'Azimut panelu',
          panel_tilt: 'Sklon panelu od horizontálnej roviny',
          correction_coefficient: 'Korekčný koeficient',
          battery_type: 'Typ batérie',
          battery_capacity_available_percentage:
            'Disponzibilná kapacita batérie',
          rated_charge: 'Nominálny nabíjací DC výkon',
          rated_discharge: 'Nominálny vybíjací DC výkon',
          max_charge: 'Maximálny nabíjací DC výkon',
          max_discharge: 'Maximálny vybíjací DC výkon',
          energy_availability_bess:
            'Dostupné množstvo energie pri 100% nom. výkonu',
          energy_availability_bess_50:
            'Dostupné množstvo energie pri 50% nom. výkonu',
          usable_energy: 'Nominálna hodnota dostupnej energie novej',
          charge_cycles: 'Predpokladaný počet cyklov',
          bat_life_expectancy: 'Predpokladaná životnosť',
          bat_efficiency: 'Obojsmerná účinnosť',
          bat_degradation_level: 'Počiatočná úroveň degradácie',
          soc: 'Miera nabitia batérie SoC',
          min_soc_end_condition: 'Minimálna hranica SoC pre optimalizáciu',
          max_soc_end_condition: 'Maximálna hranica SoC pre optimalizáciu',
          min_soc: 'Minimálna SoC z technického hľadiska',
          max_soc: 'Maximálna SoC z technického hľadiska',
          temperature_range_from: 'Teplotný rozsah od',
          temperature_range_to: 'Teplotný rozsah do',
          charging_characteristic: 'Nabíjacia charakteristika',
          discharge_characteristic: 'Vybíjacia charakteristika',
          pmax_consumption: 'Horná hranica dostupného výkonu - PMAX',
          pmin_consumption: 'Spodná hranica dostupného výkonu - PMIN',
          min_power_change_duration: 'Minimálne trvanie zmeny výkonu',
          poweroutput_change_type: 'Spôsob realizácie zmeny výkonu',
          consumption_change_unitcost: 'Ocenenie nákladov na zmenu výkonu',
          degree_of_consumption_power_change_in_stages: 'Priebeh zmeny výkonu',
          poweroutput_rate_of_change: 'Rýchlosť zmeny výkonu',
          min_poweroutput_rate_of_change: 'Minimálna rýchlosť zmeny výkonu',
          max_poweroutput_rate_of_change: 'Maximálna rýchlosť zmeny výkonu',
          degree_of_consumption_power_change: 'Velkosť stupňa',
          chart_characteristic_capacity: 'Kapacita',
          chart_characteristic_poweroutput: 'Výkon'
        },
        validation: {
          'panel_count-number': 'Hodnota musí byť celé kladné číslo',
          'charge_cycles-number': 'Hodnota musí byť celé kladné číslo',
          'value-number-range': 'Hodnota musí byť v intervale od',
          'value-number': 'Hodnota musí byť číslo',
          'latitude-number':
            'Hodnota musí byť číslo v rozmedzí od -90 do 90 s presnosťou nanajvýš 10 desatinných miest',
          'longitude-number':
            'Hodnota musí byť číslo v rozmedzí od -180 do 180 s presnosťou nanajvýš 10 desatinných miest',
          'elevation-number': 'Hodnota musí byť číslo',
          'charging_characteristic-value':
            'Prvá (0%) a posledná hodnota (100%) výkonu nabíjacej charakteristiky musí byť vyplnená',
          'discharge_characteristic-value':
            'Prvá (0%) a posledná hodnota (100%) výkonu vybíjacej charakteristiky musí byť vyplnená',
          'value-required': 'Hodnota je povinná',
          'pmax_consumption-required':
            'Horná hranica dostupného výkonu je povinná',
          'pmax_consumption-value':
            'Horná hranica dostupného výkonu musí byť väčšia alebo rovná spodnej',
          'pmin_consumption-required':
            'Spodná hranica dostupného výkonu je povinná',
          'pmin_consumption-value':
            'Spodná hranica dostupného výkonu musí byť menšia alebo rovná hornej',
          'poweroutput_change_type-required':
            'Spôsob realizácie zmeny výkonu je povinný',
          'consumption_change_unitcost-required':
            'Ocenenie nákladov na zmenu výkonu je povinné',
          'min_soc_end_condition-value':
            'Minimálna hranica SoC musí byť menšia ako maximálna hranica SoC',
          'max_soc_end_condition-value':
            'Maximálna hranica SoC musí byť väčšia ako minimálna hranica SoC',
          'min_soc-value': 'Minimálna SoC musí byť menšia ako maximálna SoC',
          'max_soc-value': 'Maximálna SoC musí byť väčšia ako minimálna SoC',
          'min_poweroutput_rate_of_change-value':
            'Minimálna rýchlosť zmeny výkonu musí byť menšia ako maximálna',
          'max_poweroutput_rate_of_change-value':
            'Maximálna rýchlosť zmeny výkonu musí byť väčšia ako minimálna'
        },
        charging_characteristic: {
          title: 'Nabíjacia charakteristika',
          input: {
            batteryCapacity: 'Kapacita',
            chargingPower: 'Výkon'
          },
          validation: {
            'chargingPower-required': 'Výkon je povinný',
            'chargingPower-number': 'Výkon musí byť číslo väčšie alebo rovné 0',
            'batteryCapacity-required': 'Kapacita je povinná'
          },
          batteryCapacity: 'Kapacita [%]',
          chargingPower: 'Výkon [kW]',
          actions: 'Akcie'
        },
        discharge_characteristic: {
          title: 'Vybíjacia charakteristika',
          input: {
            batteryCapacity: 'Kapacita',
            dischargePower: 'Výkon'
          },
          validation: {
            'dischargePower-required': 'Výkon je povinný',
            'dischargePower-number':
              'Výkon musí byť číslo menšie alebo rovné 0',
            'batteryCapacity-required': 'Kapacita je povinná'
          },
          batteryCapacity: 'Kapacita [%]',
          dischargePower: 'Výkon [kW]',
          actions: 'Akcie'
        },
        power_change_characteristic: {
          title: 'Priebeh zmeny výkonu',
          input: {
            consumptionPowerDegree: 'Veľkosť stupňa',
            changeDuration: 'Čas'
          },
          consumptionPowerDegree: 'Veľkosť stupňa [kW]',
          changeDuration: 'Čas [s]',
          actions: 'Akcie'
        }
      },
      production_model: {
        title: 'Model produkcie zariadenia'
      },
      deviceEconomicalCharacteristics: {
        title: 'Ekonomické parametre',
        input: {
          capex: 'Náklady (CAPEX)',
          opex: 'Náklady na prevádzku a údržbu (OPEX)',
          electricity_levelized_cost: 'Levelized Cost of Electricity',
          system_estimated_lifetime: 'Predpokladaná životnosť systému',
          annual_produced_energy: 'Energia vyrobená za rok',
          discount_factor: 'Discount factor',
          storage_discounted_levelized_cost:
            'Discounted levelized cost of storage'
        },
        validation: {
          'capex-number': '',
          'opex-number': ''
        }
      },
      deviceWeatherCharacteristics: {
        title: 'Údaje o počasí',
        externalWeatherMonitoringTooltip:
          'Zapnutím tejto možnosti bude využitý Solcast licenčný slot na predpoveď počasia',
        input: {
          external_weather_monitoring: 'Poskytovanie dát o počasí',
          solcast_resource_id: 'Solcast Resource ID',
          latitude: 'Zemepisná šírka',
          longitude: 'Zemepisná dĺžka'
        },
        validation: {
          'latitude-number':
            'Hodnota musí byť číslo v rozmedzí od -90 do 90 s presnosťou nanajvýš 10 desatinných miest',
          'longitude-number':
            'Hodnota musí byť číslo v rozmedzí od -180 do 180 s presnosťou nanajvýš 10 desatinných miest',
          'solcast_resource_id-match':
            'Solcast resource ID musí byť kombinácia čísel a písmen v tvare xxxx-xxxx-xxxx-xxxx'
        }
      },
      deviceReservedCapacityGuardingCharacteristics: {
        title: 'Parametre stráženia RK',
        input: {
          [CharacteristicsRk.reserved_capacity_guard_enabled]:
            'Povoliť stráženie RK',
          [CharacteristicsRk.reserved_capacity_guard_priority]:
            'Priorita stráženia RK',
          [CharacteristicsRk.reserved_capacity_max_restriction_duration]:
            'Maximálne trvanie opatrenia',
          [CharacteristicsRk.reserved_capacity_max_reduction_limit]:
            'Maximálna zmena výkonu',
          [CharacteristicsRk.reserved_capacity_max_reduction_limit_type]:
            'Typ maximálnej zmeny výkonu počas reštrikcie',
          [CharacteristicsRk.reserved_capacity_max_change_of_rate_of_power_change_reduction]:
            'Limit rýchlosti zmeny výkonu počas reštrikcie'
        },
        validation: {
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_REQUIRED]:
            'Priorita stráženia RK je povinná',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_GUARD_PRIORITY_NUMBER]:
            'Priorita stráženia RK musí byť celé číslo',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_REQUIRED]:
            'Maximálne trvanie opatrenia je povinné',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_RESTRICTION_DURATION_NUMBER]:
            'Maximálne trvanie opatrenia musí byť celé číslo',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_REQUIRED]:
            'Maximálna zmena výkonu je povinná',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_NUMBER]:
            'Maximálna zmena výkonu musí byť celé číslo',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_REDUCTION_LIMIT_TYPE_REQUIRED]:
            'Typ maximálnej zmeny výkonu počas reštrikcie je povinný',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_REQUIRED]:
            'Limit rýchlosti zmeny výkonu počas reštrikcie je povinný',
          [DeviceReservedCapacityGuardingCharacteristicsValidationArguments.RESERVED_CAPACITY_MAX_CHANGE_OF_RATE_OF_POWER_CHANGE_REDUCTION_NUMBER]:
            'Limit rýchlosti zmeny výkonu počas reštrikcie musí byť číslo'
        }
      },
      unitsOfMeasureLabels: {
        SQUARE_METRE: 'm²',
        SQUARE_MILIMETRE: 'mm²',
        PERCENT: '%',
        WATT_PEAK: 'Wp',
        EUR: '€',
        EUR_PER_KILOWATT_HOUR: '€/kWh',
        EUR_PER_MEGAWATT_HOUR: '€/MWh',
        DEGREE_CELSIA: '°C',
        WATT_PER_SQUARE_METRE: 'W/m²',
        DEGREE: '°',
        METRE: 'm',
        KWH: 'kWh',
        KW: 'kW',
        MW: 'MW',
        WATT: 'W',
        SQUARE_METRE_CUBED: '\u33A5',
        SECONDS: 's',
        EUR_PER_HOUR: '€/h',
        KW_PER_SECOND: 'kW/s',
        PERCENT_PER_DEGREE_CELSIA: '%/°C',
        YEARS: 'rokov'
      }
    },
    createDeviceCharacteristicGroupWizard: {
      title: 'Vytvorenie skupiny zariadenia',
      step01: 'Údaje skupiny',
      step02: 'Účel skupiny',
      save: 'Vytvoriť skupinu zariadenia'
    }
  }
};

export default skEaiDevices;
